import { useEffect, useRef, useState } from "react";
import { apuntarActividad, ErrorApi, leerActividad } from "../lib/api";
import { iniciales } from "../lib/formato";
import { useIdioma } from "../lib/idioma";

/**
 * Bloqueo de la pantalla cuando nadie la toca.
 *
 * En el club, la tablet del mostrador vive en la barra, a la vista de los
 * socios: el encargado se va a la trastienda un momento y ahí queda el registro
 * de socios entero —documentos, teléfonos, consumo— a un dedo de cualquiera.
 * En el panel de plataforma el motivo es otro y pesa más: esa contraseña no
 * abre un club, los abre todos.
 *
 * Tres decisiones que lo sostienen:
 *
 *  - **No cierra la sesión, la tapa.** Lo que hubiera a medias sigue ahí al
 *    volver: el carrito montado, el socio elegido, el diálogo abierto. Un
 *    bloqueo que te obliga a empezar de cero cada vez que suena el teléfono
 *    dura una semana en el local.
 *  - **Cuenta desde el último gesto, no desde que entraste.** Mientras se
 *    trabaja no aparece nunca.
 *  - **Aguanta una recarga.** La hora del último gesto se guarda en el
 *    navegador, no solo en memoria: si estuviera solo en memoria bastaría con
 *    pulsar F5 delante de la pantalla bloqueada para entrar sin contraseña.
 *
 * Lo que esto NO es: cifrado. El token sigue en el navegador y quien sepa abrir
 * las herramientas de desarrollo lo saca. Es una barrera contra quien se apoya
 * en la barra y curiosea, que es justo el riesgo que hay.
 *
 * La pieza es la misma para las dos aplicaciones; lo único que cambia es a quién
 * se le pregunta la contraseña, que llega como `comprobar`.
 */

/** Dos minutos sin tocar nada. */
export const MINUTOS_HASTA_BLOQUEAR = 2;

/** Cada cuánto se mira el reloj. No hace falta más fino. */
const CADA_CUANTO_SE_MIRA_MS = 5_000;

const GESTOS = ["mousedown", "keydown", "touchstart", "wheel", "scroll"] as const;

export default function BloqueoInactividad({
  nombre,
  subtitulo,
  comprobar,
  alSalir,
}: {
  nombre: string;
  subtitulo?: string;
  /** Comprueba la contraseña contra quien corresponda. Lanza si no vale. */
  comprobar: (password: string) => Promise<unknown>;
  alSalir: () => void;
}) {
  /*
   * Al cargar la página se mira la hora guardada: si el último gesto es más
   * viejo que el límite, la pantalla aparece ya bloqueada. Sin esto, recargar
   * era la forma de saltárselo.
   */
  const [bloqueado, setBloqueado] = useState(() => {
    const ultimo = leerActividad();
    return ultimo > 0 && Date.now() - ultimo >= MINUTOS_HASTA_BLOQUEAR * 60_000;
  });
  const ultimoGesto = useRef(Date.now());

  useEffect(() => {
    /*
     * Se apunta la hora del último gesto y se mira cada pocos segundos, en vez
     * de reprogramar un temporizador en cada movimiento: en una tablet táctil
     * los eventos llegan a decenas por segundo y no hay por qué pagarlos. Al
     * navegador se baja en ese mismo repaso, por lo mismo.
     */
    const apuntar = () => {
      ultimoGesto.current = Date.now();
    };
    for (const gesto of GESTOS) window.addEventListener(gesto, apuntar, { passive: true });

    const reloj = setInterval(() => {
      /*
       * Con la pantalla tapada no se toca la hora guardada: teclear la
       * contraseña son gestos, y refrescarla dejaría que recargar a media
       * escritura abriera la sesión.
       */
      if (bloqueado) return;
      if (Date.now() - ultimoGesto.current >= MINUTOS_HASTA_BLOQUEAR * 60_000) setBloqueado(true);
      else apuntarActividad(ultimoGesto.current);
    }, CADA_CUANTO_SE_MIRA_MS);

    return () => {
      for (const gesto of GESTOS) window.removeEventListener(gesto, apuntar);
      clearInterval(reloj);
    };
  }, [bloqueado]);

  if (!bloqueado) return null;

  return (
    <PantallaBloqueo
      nombre={nombre}
      subtitulo={subtitulo}
      comprobar={comprobar}
      alDesbloquear={() => {
        ultimoGesto.current = Date.now();
        apuntarActividad();
        setBloqueado(false);
      }}
      alSalir={alSalir}
    />
  );
}

function PantallaBloqueo({
  nombre,
  subtitulo,
  comprobar,
  alDesbloquear,
  alSalir,
}: {
  nombre: string;
  subtitulo?: string;
  comprobar: (password: string) => Promise<unknown>;
  alDesbloquear: () => void;
  alSalir: () => void;
}) {
  const { t } = useIdioma();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (!password) return;
    setEnviando(true);
    setError("");
    try {
      await comprobar(password);
      setPassword("");
      alDesbloquear();
    } catch (fallo) {
      /*
       * Si se agotan los intentos, se cierra la sesión del todo. Dejar la
       * pantalla ahí sería convertirla en un sitio cómodo para ir probando
       * contraseñas sobre una sesión que ya está abierta.
       */
      if (fallo instanceof ErrorApi && fallo.status === 429) {
        alSalir();
        return;
      }
      setError(fallo instanceof Error ? fallo.message : t("bl.fallo"));
      setPassword("");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="bloqueo">
      <form className="bloqueo-tarjeta" onSubmit={enviar}>
        <div className="avatar grande">{iniciales(nombre)}</div>
        <h2>{nombre}</h2>
        {subtitulo && <p className="suave pequeno">{subtitulo}</p>}

        <p className="bloqueo-explicacion">
          {t("bl.explicacion")}
        </p>

        {error && <div className="aviso error">{error}</div>}

        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t("bl.tuContrasena")}
          autoFocus
          autoComplete="current-password"
        />

        <button className="boton grande bloque" disabled={enviando || !password}>
          {enviando ? t("bl.comprobando") : t("bl.continuar")}
        </button>

        <button type="button" className="boton fantasma" onClick={alSalir}>
          {t("bl.salirOtraCuenta")}
        </button>
      </form>
    </div>
  );
}
